import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { 
  ArrowLeft, Calendar, Clock, User, MapPin, Phone, Mail,
  CheckCircle, XCircle, AlertCircle, Eye, ChevronLeft, ChevronRight,
  Filter, Search
} from 'lucide-react';

const CaregiverBookingsSchedule = () => {
  const { user } = useAuth();
  const { bookings } = useData();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [expandedId, setExpandedId] = useState(null);

  // Filter bookings for this caregiver
  const caregiverBookings = useMemo(() => {
    const userId = user?.uid || user?.id;
    return bookings.filter(b => b.caregiverId === userId);
  }, [bookings, user]);

  // Apply filters
  const filteredBookings = useMemo(() => {
    let filtered = [...caregiverBookings];

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(b => b.status === filterStatus);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        b.ownerName?.toLowerCase().includes(searchLower) ||
        b.petName?.toLowerCase().includes(searchLower) ||
        b.service?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by start date (nearest first)
    filtered.sort((a, b) => new Date(a.startDate || a.start) - new Date(b.startDate || b.start));

    return filtered;
  }, [caregiverBookings, filterStatus, searchTerm]);

  // Group bookings by status
  const bookingsByStatus = useMemo(() => {
    return {
      pending: filteredBookings.filter(b => b.status === 'pending' || b.status === 'requested'),
      accepted: filteredBookings.filter(b => b.status === 'accepted' || b.status === 'confirmed'),
      inProgress: filteredBookings.filter(b => b.status === 'in_progress'),
      completed: filteredBookings.filter(b => b.status === 'completed'),
      cancelled: filteredBookings.filter(b => b.status === 'cancelled')
    };
  }, [filteredBookings]);

  // Calendar logic
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    return { daysInMonth, startDayOfWeek, year, month };
  };

  const getBookingsForDate = (date) => {
    return filteredBookings.filter(booking => {
      const startDate = new Date(booking.startDate || booking.start);
      const endDate = new Date(booking.endDate || booking.end);
      return date >= startDate && date <= endDate;
    });
  };

  const { daysInMonth, startDayOfWeek, year, month } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'requested': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'accepted': 'bg-blue-100 text-blue-800 border-blue-200',
      'confirmed': 'bg-blue-100 text-blue-800 border-blue-200',
      'in_progress': 'bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] border-[color-mix(in_srgb,var(--pf-accent)_25%,var(--pf-border))]',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': AlertCircle,
      'requested': AlertCircle,
      'accepted': CheckCircle,
      'confirmed': CheckCircle,
      'in_progress': Clock,
      'completed': CheckCircle,
      'cancelled': XCircle
    };
    const Icon = icons[status] || AlertCircle;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/caregiver-dashboard" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Bookings & Schedule</h1>
              <p className="text-gray-600 mt-2">Manage your appointments and schedule</p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[var(--pf-accent)] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                List View
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-[var(--pf-accent)] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Calendar View
              </button>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by owner, pet, or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
              >
                <option value="all">All Status ({caregiverBookings.length})</option>
                <option value="pending">Pending ({bookingsByStatus.pending.length})</option>
                <option value="accepted">Accepted ({bookingsByStatus.accepted.length})</option>
                <option value="in_progress">In Progress ({bookingsByStatus.inProgress.length})</option>
                <option value="completed">Completed ({bookingsByStatus.completed.length})</option>
                <option value="cancelled">Cancelled ({bookingsByStatus.cancelled.length})</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700 font-medium">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">{bookingsByStatus.pending.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Accepted</p>
                <p className="text-2xl font-bold text-blue-900">{bookingsByStatus.accepted.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="pf-organic-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--pf-accent)] font-medium">In Progress</p>
                <p className="text-2xl font-bold text-[var(--pf-ink)]">{bookingsByStatus.inProgress.length}</p>
              </div>
              <Clock className="w-8 h-8 text-[var(--pf-accent)]" />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium">Completed</p>
                <p className="text-2xl font-bold text-green-900">{bookingsByStatus.completed.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-medium">Cancelled</p>
                <p className="text-2xl font-bold text-red-900">{bookingsByStatus.cancelled.length}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === 'list' ? (
          /* List View */
          <div className="space-y-4">
            {filteredBookings.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No bookings found</h3>
                <p className="text-gray-600">
                  {searchTerm || filterStatus !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Your bookings will appear here once owners book your services'}
                </p>
              </div>
            ) : (
              filteredBookings.map((booking) => (
                <div key={booking.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-1 ${getStatusColor(booking.status)}`}>
                          {getStatusIcon(booking.status)}
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ')}
                        </div>
                        <span className="text-sm text-gray-500">
                          Booking #{booking.id.slice(0, 8)}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{booking.service || 'Pet Care Service'}</h3>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{booking.ownerName || 'Owner'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="font-medium">Pet:</span>
                          <span>{booking.petName || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(booking.startDate || booking.start).toLocaleDateString()} - 
                            {new Date(booking.endDate || booking.end).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{booking.duration || 'N/A'}</span>
                        </div>
                      </div>

                      {booking.specialRequests && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-900">
                            <span className="font-medium">Special Requests:</span> {booking.specialRequests}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="ml-6 text-right">
                      <div className="text-2xl font-bold text-gray-900 mb-2">
                        ₹{booking.totalCost?.toLocaleString() || '0'}
                      </div>
                      <button
                        onClick={() => setExpandedId(expandedId === booking.id ? null : booking.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--pf-accent)] text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
                        aria-expanded={expandedId === booking.id}
                      >
                        <Eye className="w-4 h-4" />
                        {expandedId === booking.id ? 'Hide Details' : 'View Details'}
                      </button>
                    </div>
                  </div>

                  {expandedId === booking.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Booking ID</p>
                        <p className="font-medium text-gray-900 break-all">{booking.id}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium text-gray-900">{booking.location || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Pet</p>
                        <p className="font-medium text-gray-900">
                          {booking.petName || 'N/A'}
                          {booking.petBreed ? ` · ${booking.petBreed}` : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Owner</p>
                        <p className="font-medium text-gray-900">{booking.ownerName || 'Owner'}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-gray-500">Notes</p>
                        <p className="font-medium text-gray-900">
                          {booking.specialRequests || booking.notes || 'No special requirements'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          /* Calendar View */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <h2 className="text-xl font-bold text-gray-900">
                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>

              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-700 py-2">
                  {day}
                </div>
              ))}

              {/* Empty cells for days before month starts */}
              {Array.from({ length: startDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}

              {/* Calendar days */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const date = new Date(year, month, day);
                const bookingsForDay = getBookingsForDate(date);
                const isToday = date.toDateString() === new Date().toDateString();

                return (
                  <div
                    key={day}
                    className={`aspect-square border rounded-lg p-2 ${
                      isToday ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)]' : 'border-gray-200'
                    } ${bookingsForDay.length > 0 ? 'bg-blue-50' : 'bg-white'}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[var(--pf-accent)]' : 'text-gray-900'}`}>
                      {day}
                    </div>
                    {bookingsForDay.slice(0, 2).map((booking, idx) => (
                      <div
                        key={idx}
                        className={`text-xs px-1 py-0.5 rounded mb-1 truncate ${getStatusColor(booking.status)}`}
                        title={`${booking.service} - ${booking.ownerName}`}
                      >
                        {booking.petName || 'Booking'}
                      </div>
                    ))}
                    {bookingsForDay.length > 2 && (
                      <div className="text-xs text-gray-600 font-medium">
                        +{bookingsForDay.length - 2} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Calendar Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[var(--pf-accent-soft)] border-2 border-[var(--pf-accent)] rounded"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-50 rounded"></div>
                <span className="text-gray-600">Has Bookings</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaregiverBookingsSchedule;

